/**
 * Netblitz pricing packages - single source of truth (NL/EN/FR/DE)
 * Used by PricingPage, PricingPreview and package CTAs.
 */

export const pricingPackages = {
  nl: [
    {
      slug: 'starter',
      name: 'Starter Website',
      subtitle: 'Voor zelfstandigen en startende ondernemingen die professioneel online zichtbaar willen zijn. Een moderne one-page website waarop bezoekers direct zien wie je bent, wat je doet en hoe ze contact kunnen opnemen.',
      price: 'vanaf €599',
      monthlyPrice: 'vanaf €29/maand',
      monthlyNote: 'Inclusief hosting, domeinbeheer, technische updates, back-ups en ondersteuning.',
      popular: false,
      cta: 'Start met mijn website',
      features: [
        'Professioneel maatwerkdesign',
        '1 pagina met maximaal 4 hoofdsecties',
        'Mobiel, tablet en desktop geoptimaliseerd',
        'Basis SEO-structuur',
        'Contactformulier of WhatsApp-koppeling',
        'SSL-beveiliging',
        '1 correctieronde',
      ],
      featuresShort: [
        'Professioneel maatwerkdesign',
        '1 pagina met max. 4 hoofdsecties',
        'Mobiel, tablet en desktop geoptimaliseerd',
        'Basis SEO-structuur',
        'Contactformulier of WhatsApp',
      ],
    },
    {
      slug: 'business',
      name: 'Business Website',
      subtitle: 'Voor bedrijven die online beter gevonden willen worden en meer aanvragen uit hun website willen halen. Een complete bedrijfswebsite met meerdere pagina\u2019s, sterke structuur en aandacht voor vindbaarheid en conversie.',
      price: 'vanaf €1.099',
      monthlyPrice: 'vanaf €39/maand',
      monthlyNote: 'Inclusief hosting, domeinbeheer, technische updates, back-ups en ondersteuning.',
      popular: true,
      cta: 'Vraag mijn Business Website aan',
      features: [
        'Alles uit Starter',
        '4 tot 8 professionele pagina\u2019s',
        'Design afgestemd op jouw onderneming en sector',
        'Uitgebreidere SEO-structuur',
        'Conversiegerichte contact- en offerteflow',
        'Integratie van formulieren en WhatsApp',
        'Meertaligheid mogelijk',
        'Google Analytics / meetmogelijkheden',
        '2 correctierondes',
      ],
      featuresShort: [
        'Alles uit Starter',
        '4 tot 8 professionele pagina\u2019s',
        'Design op maat van jouw sector',
        'Uitgebreidere SEO-structuur',
        'Meertaligheid + Google Analytics',
      ],
    },
    {
      slug: 'portal',
      name: 'Business Portal',
      subtitle: 'Voor bedrijven die hun klanten ook digitaal service willen bieden. Combineer je professionele website met een beveiligde klantomgeving waarin klanten documenten en informatie kunnen raadplegen.',
      price: 'vanaf €1.995',
      monthlyPrice: 'vanaf €49/maand',
      monthlyNote: 'Inclusief websitehosting, portalhosting, updates, back-ups en technische ondersteuning.',
      popular: false,
      cta: 'Bespreek mijn klantportaal',
      features: [
        'Alles uit Business Website',
        'Persoonlijke klantlogin',
        'Beveiligde klantomgeving',
        'Documenten digitaal beschikbaar maken',
        'Offertes en klantinformatie online',
        'Verschillende gebruikersrechten',
        'Beheeromgeving voor jouw bedrijf',
        'Mobielvriendelijke portal',
        '2 tot 3 correctierondes',
      ],
      featuresShort: [
        'Alles uit Business Website',
        'Persoonlijke klantlogin',
        'Beveiligde klantomgeving',
        'Documenten & offertes online',
        'Mobielvriendelijke portal',
      ],
    },
    {
      slug: 'pro',
      name: 'Business Pro',
      subtitle: 'Voor dienstverlenende bedrijven die website, planning en dagelijkse administratie willen combineren. Business Pro geeft je een digitale bedrijfsomgeving waarin medewerkers en administratie vanuit één systeem samenwerken.',
      price: 'vanaf €2.995',
      monthlyPrice: 'vanaf €79/maand',
      monthlyNote: 'Inclusief website, applicatiehosting, updates, back-ups en technische ondersteuning.',
      popular: false,
      cta: 'Bespreek mijn bedrijfsoplossing',
      features: [
        'Alles uit Business Website',
        'Digitale planning',
        'Medewerkers- en opdrachtenbeheer',
        'Werkbonnen',
        'Foto\u2019s toevoegen aan opdrachten',
        'Urenregistratie per medewerker of opdracht',
        'Dashboard met actuele overzichten',
        'Mobiel gebruik voor medewerkers',
        'Beheeromgeving voor administratie',
        'Uitbreidbaar met aanvullende bedrijfsfuncties',
      ],
      featuresShort: [
        'Alles uit Business Website',
        'Digitale planning & opdrachten',
        'Werkbonnen met foto\u2019s',
        'Urenregistratie per medewerker',
        'Dashboard met overzichten',
      ],
    },
  ],
  en: [
    {
      slug: 'starter',
      name: 'Starter Website',
      subtitle: 'For freelancers and starting businesses who want a professional online presence. A modern one-page website where visitors instantly see who you are, what you do and how to reach you.',
      price: 'from €599',
      monthlyPrice: 'from €29/month',
      monthlyNote: 'Includes hosting, domain management, technical updates, backups and support.',
      popular: false,
      cta: 'Start my website',
      features: [
        'Professional custom design',
        '1 page with up to 4 main sections',
        'Optimized for mobile, tablet and desktop',
        'Basic SEO structure',
        'Contact form or WhatsApp link',
        'SSL security',
        '1 revision round',
      ],
      featuresShort: [
        'Professional custom design',
        '1 page with up to 4 sections',
        'Mobile, tablet & desktop optimized',
        'Basic SEO structure',
        'Contact form or WhatsApp',
      ],
    },
    {
      slug: 'business',
      name: 'Business Website',
      subtitle: 'For businesses that want to be found better online and get more inquiries from their website. A complete company website with multiple pages, strong structure and focus on visibility and conversion.',
      price: 'from €1,099',
      monthlyPrice: 'from €39/month',
      monthlyNote: 'Includes hosting, domain management, technical updates, backups and support.',
      popular: true,
      cta: 'Request my Business Website',
      features: [
        'Everything from Starter',
        '4 to 8 professional pages',
        'Design tailored to your company and sector',
        'Extended SEO structure',
        'Conversion-focused contact & quote flow',
        'Forms and WhatsApp integration',
        'Multilingual possible',
        'Google Analytics / measurement options',
        '2 revision rounds',
      ],
      featuresShort: [
        'Everything from Starter',
        '4 to 8 professional pages',
        'Design tailored to your sector',
        'Extended SEO structure',
        'Multilingual + Google Analytics',
      ],
    },
    {
      slug: 'portal',
      name: 'Business Portal',
      subtitle: 'For companies that want to serve their clients digitally too. Combine your professional website with a secure client environment where clients can view documents and information.',
      price: 'from €1,995',
      monthlyPrice: 'from €49/month',
      monthlyNote: 'Includes website hosting, portal hosting, updates, backups and technical support.',
      popular: false,
      cta: 'Discuss my client portal',
      features: [
        'Everything from Business Website',
        'Personal client login',
        'Secure client environment',
        'Make documents available digitally',
        'Quotes and client info online',
        'Different user permissions',
        'Management environment for your business',
        'Mobile-friendly portal',
        '2 to 3 revision rounds',
      ],
      featuresShort: [
        'Everything from Business Website',
        'Personal client login',
        'Secure client environment',
        'Documents & quotes online',
        'Mobile-friendly portal',
      ],
    },
    {
      slug: 'pro',
      name: 'Business Pro',
      subtitle: 'For service companies that want to combine website, planning and daily administration. Business Pro gives you a digital business environment where staff and administration work together from one system.',
      price: 'from €2,995',
      monthlyPrice: 'from €79/month',
      monthlyNote: 'Includes website, application hosting, updates, backups and technical support.',
      popular: false,
      cta: 'Discuss my business solution',
      features: [
        'Everything from Business Website',
        'Digital planning',
        'Staff and assignment management',
        'Work orders',
        'Add photos to assignments',
        'Time registration per employee or assignment',
        'Dashboard with live overviews',
        'Mobile use for employees',
        'Management environment for administration',
        'Expandable with additional business functions',
      ],
      featuresShort: [
        'Everything from Business Website',
        'Digital planning & assignments',
        'Work orders with photos',
        'Time registration per employee',
        'Dashboard with overviews',
      ],
    },
  ],
  fr: [
    {
      slug: 'starter',
      name: 'Starter Website',
      subtitle: 'Pour les indépendants et jeunes entreprises qui veulent une présence en ligne professionnelle. Un site one-page moderne où les visiteurs voient directement qui vous êtes, ce que vous faites et comment vous contacter.',
      price: 'à partir de €599',
      monthlyPrice: 'à partir de €29/mois',
      monthlyNote: 'Inclut hébergement, gestion du domaine, mises à jour techniques, sauvegardes et support.',
      popular: false,
      cta: 'Démarrer mon site',
      features: [
        'Design professionnel sur mesure',
        '1 page avec 4 sections principales max.',
        'Optimisé mobile, tablette et desktop',
        'Structure SEO de base',
        'Formulaire de contact ou lien WhatsApp',
        'Sécurité SSL',
        '1 tour de correction',
      ],
      featuresShort: [
        'Design professionnel sur mesure',
        '1 page, 4 sections max.',
        'Mobile, tablette & desktop',
        'Structure SEO de base',
        'Formulaire ou WhatsApp',
      ],
    },
    {
      slug: 'business',
      name: 'Business Website',
      subtitle: 'Pour les entreprises qui veulent être mieux trouvées en ligne et obtenir plus de demandes. Un site complet avec plusieurs pages, une structure solide et un focus sur la visibilité et la conversion.',
      price: 'à partir de €1.099',
      monthlyPrice: 'à partir de €39/mois',
      monthlyNote: 'Inclut hébergement, gestion du domaine, mises à jour techniques, sauvegardes et support.',
      popular: true,
      cta: 'Demander mon site Business',
      features: [
        'Tout de Starter',
        '4 à 8 pages professionnelles',
        'Design adapté à votre entreprise et secteur',
        'Structure SEO étendue',
        'Flux contact et devis orienté conversion',
        'Intégration formulaires et WhatsApp',
        'Multilingue possible',
        'Google Analytics / mesures',
        '2 tours de correction',
      ],
      featuresShort: [
        'Tout de Starter',
        '4 à 8 pages professionnelles',
        'Design adapté à votre secteur',
        'Structure SEO étendue',
        'Multilingue + Google Analytics',
      ],
    },
    {
      slug: 'portal',
      name: 'Business Portal',
      subtitle: 'Pour les entreprises qui veulent aussi un service client digital. Combinez votre site professionnel avec un espace client sécurisé où les clients consultent documents et informations.',
      price: 'à partir de €1.995',
      monthlyPrice: 'à partir de €49/mois',
      monthlyNote: 'Inclut hébergement site, hébergement portail, mises à jour, sauvegardes et support technique.',
      popular: false,
      cta: 'Discuter de mon portail client',
      features: [
        'Tout du site Business',
        'Login client personnel',
        'Espace client sécurisé',
        'Documents disponibles en ligne',
        'Devis et infos clients en ligne',
        'Différents droits d\u2019accès',
        'Environnement de gestion pour votre entreprise',
        'Portail adapté au mobile',
        '2 à 3 tours de correction',
      ],
      featuresShort: [
        'Tout du site Business',
        'Login client personnel',
        'Espace client sécurisé',
        'Documents & devis en ligne',
        'Portail adapté au mobile',
      ],
    },
    {
      slug: 'pro',
      name: 'Business Pro',
      subtitle: 'Pour les prestataires de services qui veulent combiner site, planning et administration quotidienne. Business Pro vous offre un environnement digital où équipe et administration collaborent depuis un seul système.',
      price: 'à partir de €2.995',
      monthlyPrice: 'à partir de €79/mois',
      monthlyNote: 'Inclut site, hébergement application, mises à jour, sauvegardes et support technique.',
      popular: false,
      cta: 'Discuter de ma solution',
      features: [
        'Tout du site Business',
        'Planning digital',
        'Gestion du personnel et des missions',
        'Bons de travail',
        'Ajout de photos aux missions',
        'Suivi des heures par employé ou mission',
        'Tableau de bord avec aperçus en temps réel',
        'Utilisation mobile pour les employés',
        'Environnement de gestion administrative',
        'Extensible avec fonctions supplémentaires',
      ],
      featuresShort: [
        'Tout du site Business',
        'Planning digital & missions',
        'Bons de travail avec photos',
        'Suivi des heures par employé',
        'Tableau de bord avec aperçus',
      ],
    },
  ],
  de: [
    {
      slug: 'starter',
      name: 'Starter Website',
      subtitle: 'Für Selbstständige und junge Unternehmen, die professionell online sichtbar sein wollen. Eine moderne One-Page-Website, auf der Besucher sofort sehen, wer Sie sind, was Sie tun und wie sie Kontakt aufnehmen.',
      price: 'ab €599',
      monthlyPrice: 'ab €29/Monat',
      monthlyNote: 'Inklusive Hosting, Domainverwaltung, technische Updates, Backups und Support.',
      popular: false,
      cta: 'Meine Website starten',
      features: [
        'Professionelles maßgeschneidertes Design',
        '1 Seite mit maximal 4 Hauptsektionen',
        'Für Mobil, Tablet und Desktop optimiert',
        'Basis-SEO-Struktur',
        'Kontaktformular oder WhatsApp-Verknüpfung',
        'SSL-Sicherheit',
        '1 Korrekturrunde',
      ],
      featuresShort: [
        'Maßgeschneidertes Design',
        '1 Seite, max. 4 Sektionen',
        'Mobil, Tablet & Desktop',
        'Basis-SEO-Struktur',
        'Kontaktformular oder WhatsApp',
      ],
    },
    {
      slug: 'business',
      name: 'Business Website',
      subtitle: 'Für Unternehmen, die online besser gefunden werden und mehr Anfragen über ihre Website erhalten wollen. Eine komplette Website mit mehreren Seiten, starker Struktur und Fokus auf Sichtbarkeit und Conversion.',
      price: 'ab €1.099',
      monthlyPrice: 'ab €39/Monat',
      monthlyNote: 'Inklusive Hosting, Domainverwaltung, technische Updates, Backups und Support.',
      popular: true,
      cta: 'Meine Business-Website anfragen',
      features: [
        'Alles aus Starter',
        '4 bis 8 professionelle Seiten',
        'Design abgestimmt auf Ihr Unternehmen und Ihre Branche',
        'Erweiterte SEO-Struktur',
        'Conversion-orientierter Kontakt- und Angebotsflow',
        'Integration von Formularen und WhatsApp',
        'Mehrsprachigkeit möglich',
        'Google Analytics / Messmöglichkeiten',
        '2 Korrekturrunden',
      ],
      featuresShort: [
        'Alles aus Starter',
        '4 bis 8 professionelle Seiten',
        'Design für Ihre Branche',
        'Erweiterte SEO-Struktur',
        'Mehrsprachig + Google Analytics',
      ],
    },
    {
      slug: 'portal',
      name: 'Business Portal',
      subtitle: 'Für Unternehmen, die ihren Kunden auch digital Service bieten wollen. Kombinieren Sie Ihre Website mit einem sicheren Kundenbereich, in dem Kunden Dokumente und Informationen einsehen.',
      price: 'ab €1.995',
      monthlyPrice: 'ab €49/Monat',
      monthlyNote: 'Inklusive Website-Hosting, Portal-Hosting, Updates, Backups und technischer Support.',
      popular: false,
      cta: 'Mein Kundenportal besprechen',
      features: [
        'Alles aus Business Website',
        'Persönlicher Kunden-Login',
        'Sicherer Kundenbereich',
        'Dokumente digital verfügbar machen',
        'Angebote und Kundeninfos online',
        'Verschiedene Benutzerrechte',
        'Verwaltungsumgebung für Ihr Unternehmen',
        'Mobilfreundliches Portal',
        '2 bis 3 Korrekturrunden',
      ],
      featuresShort: [
        'Alles aus Business Website',
        'Persönlicher Kunden-Login',
        'Sicherer Kundenbereich',
        'Dokumente & Angebote online',
        'Mobilfreundliches Portal',
      ],
    },
    {
      slug: 'pro',
      name: 'Business Pro',
      subtitle: 'Für Dienstleister, die Website, Planung und tägliche Verwaltung kombinieren wollen. Business Pro gibt Ihnen eine digitale Unternehmensumgebung, in der Mitarbeiter und Verwaltung aus einem System zusammenarbeiten.',
      price: 'ab €2.995',
      monthlyPrice: 'ab €79/Monat',
      monthlyNote: 'Inklusive Website, Applikations-Hosting, Updates, Backups und technischer Support.',
      popular: false,
      cta: 'Meine Unternehmenslösung besprechen',
      features: [
        'Alles aus Business Website',
        'Digitale Planung',
        'Mitarbeiter- und Auftragsverwaltung',
        'Arbeitsscheine',
        'Fotos zu Aufträgen hinzufügen',
        'Zeiterfassung pro Mitarbeiter oder Auftrag',
        'Dashboard mit aktuellen Übersichten',
        'Mobile Nutzung für Mitarbeiter',
        'Verwaltungsumgebung für die Administration',
        'Erweiterbar mit zusätzlichen Funktionen',
      ],
      featuresShort: [
        'Alles aus Business Website',
        'Digitale Planung & Aufträge',
        'Arbeitsscheine mit Fotos',
        'Zeiterfassung pro Mitarbeiter',
        'Dashboard mit Übersichten',
      ],
    },
  ],
};

export default pricingPackages;
